// ────────────────────────────────────────────────────────────
// PERSISTENCE — now backed by Cloudflare Pages Functions / D1 / R2
// ────────────────────────────────────────────────────────────
// Same interface as the old IndexedDB version so component code
// doesn't change. Per-deal data is loaded as a bundle in one call
// (see loadDealBundle) for efficiency.

import { dealsApi, entitiesApi, type DealBundle } from './api';
import type {
  Deal, DealFile, TitleFinding, ChainLink, MathWorksheet, TitleFlag,
  OwnershipPicture, Tract, Comp, LeadEvent, PackageDraft,
  BuyerMatch, ConversationMessage, ActivityEvent, UIState,
} from '../types';

// ─── DEALS ──────────────────────────────────────────────────
export const dealsRepo = {
  async list(): Promise<Deal[]> {
    const r = await dealsApi.list();
    return r.deals;
  },
  async get(id: string): Promise<Deal | undefined> {
    try { return (await dealsApi.load(id)).deal; } catch { return undefined; }
  },
  async save(d: Deal): Promise<void> { await dealsApi.save(d); },
  async remove(id: string): Promise<void> { await dealsApi.remove(id); },
};

// ─── BUNDLE LOADER ─────────────────────────────────────────
// Used by the deals store to load everything for a deal in one call.
export async function loadDealBundle(id: string): Promise<DealBundle> {
  return dealsApi.load(id);
}

// ─── PER-ENTITY WRITES ─────────────────────────────────────
// Each maps to the generic /api/deals/:id/entities endpoint.
export const filesRepo = {
  async save(f: DealFile): Promise<void> { await entitiesApi.upsert(f.dealId, 'file', f); },
};

export const titleRepo = {
  async saveFinding(f: TitleFinding): Promise<void> { await entitiesApi.upsert(f.dealId, 'finding', f); },
  async saveChainLink(c: ChainLink): Promise<void> { await entitiesApi.upsert(c.dealId, 'chain', c); },
  async saveMath(m: MathWorksheet): Promise<void> { await entitiesApi.upsert(m.dealId, 'math', m); },
  async saveFlag(f: TitleFlag): Promise<void> { await entitiesApi.upsert(f.dealId, 'flag', f); },
};

export const overviewRepo = {
  async savePicture(p: OwnershipPicture): Promise<void> { await entitiesApi.upsert(p.dealId, 'picture', p); },
  async saveTract(t: Tract): Promise<void> { await entitiesApi.upsert(t.dealId, 'tract', t); },
};

export const compsRepo = {
  async save(c: Comp): Promise<void> { await entitiesApi.upsert(c.dealId, 'comp', c); },
};

export const leadHistoryRepo = {
  async save(e: LeadEvent): Promise<void> { await entitiesApi.upsert(e.dealId, 'leadEvent', e); },
};

export const packageRepo = {
  async save(p: PackageDraft): Promise<void> { await entitiesApi.upsert(p.dealId, 'package', p); },
};

export const buyersRepo = {
  async saveMatches(dealId: string, matches: BuyerMatch[]): Promise<void> {
    for (const m of matches) await entitiesApi.upsert(dealId, 'match', m);
  },
};

export const conversationRepo = {
  async save(dealId: string, msgs: ConversationMessage[]): Promise<void> {
    await entitiesApi.replaceConversation(dealId, msgs);
  },
  async append(msg: ConversationMessage): Promise<void> {
    await entitiesApi.upsert(msg.dealId, 'message', msg);
  },
};

export const activityRepo = {
  async append(e: ActivityEvent): Promise<void> { await entitiesApi.upsert(e.dealId, 'activity', e); },
};

export const uiRepo = {
  async load(): Promise<UIState | undefined> {
    try { const s = localStorage.getItem('tlb:ui'); return s ? JSON.parse(s) : undefined; } catch { return undefined; }
  },
  async save(u: UIState): Promise<void> {
    try { localStorage.setItem('tlb:ui', JSON.stringify(u)); } catch {}
  },
};

export async function isFreshInstall(): Promise<boolean> {
  const deals = await dealsRepo.list();
  return deals.length === 0;
}
export async function nukeAll(): Promise<void> {
  console.warn('nukeAll() is not implemented in cloud mode');
}
